import React, { useCallback } from "react";
import { QueryClient, QueryKey, useQueryClient } from "@tanstack/react-query";
import { noop } from "lodash";

import { assert } from "../../../../commons/libs/assert";
import { compactPromiseErrors as promiseSequence } from "../../../../commons/libs/promise";
import { getSpecConfig } from "../../../../commons/specs";
import {
  ActiveFilters,
  FilterKey,
  getFilterKeysForFilterItem,
  getTypedFilterConfigEntries,
  getTypedFilterConfigKeys,
  ProductFilterValues
} from "../../../../commons/specs/filters";
import { ProductSpecKey, ProductType } from "../../../../commons/specs/product";
import { Currency } from "../../../../commons/types/currency";
import { GlobalLocationState } from "../../../../commons/types/location";
import { AssortmentPriceSettings, ProductFinderAssortmentFilterSettings } from "../../../../commons/types/settings";
import { useCommonAppDispatcher, useCommonAppSelector } from "../../store/hooks";
import { toStoreFilters } from "../../store/slices/session";
import { getPossibleValues as getPossibleValuesLib } from "../content-service";
import {
  clearActiveFilters,
  clearAllActiveFilters,
  countActiveFilters,
  getEvaluatedDefaultActiveFilters,
  hasActiveFilters,
  isFilterItemActive as isFilterItemActiveLib,
  showsOnlyEBikes
} from "../filters";
import { useAppLocation } from "../hooks/use-app-routing";
import useGetFilterKeysWithInsufficientFilterValues from "../hooks/use-get-filter-keys-with-insufficient-filter-values";
import useQuery, { UseQueryOptions } from "../hooks/use-query";
import useSelectedBrands from "../hooks/use-selected-brands";

import { useSettings } from "./use-settings";

interface Arguments<Type extends ProductType> {
  productType: Type;
  assortmentFilterSettings: ProductFinderAssortmentFilterSettings;
  brandKey?: string;
  useSessionState?: boolean;
  skipDefaultActiveFilters?: boolean;
  options?: Omit<UseQueryOptions<ProductFilterValues<Type>>, "queryKey" | "queryFn">;
}

const useProductFilter = <Type extends ProductType>({
  productType,
  assortmentFilterSettings,
  brandKey = "",
  useSessionState = true,
  skipDefaultActiveFilters = false,
  options = {}
}: Arguments<Type>) => {
  const settings = useSettings();

  const queryClient = useQueryClient();
  const specConfig = getSpecConfig(productType);
  const filterKeys = getTypedFilterConfigKeys(specConfig);

  const [activeFilters, setActiveFilters] = useSessionOrLocalState<Type>(useSessionState);
  const [wereDefaultActiveFiltersEvaluated, setWereDefaultActiveFiltersEvaluated] = React.useState<boolean>(false);
  const selectedBrands = useSelectedBrands({ activeFilters, brandKey });

  const query = useQuery({
    ...options,
    queryKey: queryKey({
      brandKey,
      currency: settings.initialized.currency,
      assortmentPriceSettings: settings.assortmentPriceSettings,
      filterKeys
    }),
    queryFn: () =>
      getPossibleValues({
        brandKey,
        currency: settings.initialized.currency,
        assortmentPriceSettings: settings.assortmentPriceSettings,
        filterKeys
      }),
    onSuccess: data => {
      options?.onSuccess?.(data);
      if (!wereDefaultActiveFiltersEvaluated) {
        if (skipDefaultActiveFilters) {
          setWereDefaultActiveFiltersEvaluated(true);
        } else if (activeFilters !== null) {
          setWereDefaultActiveFiltersEvaluated(true);
        } else {
          setDefaultActiveFilters();
          setWereDefaultActiveFiltersEvaluated(true);
        }
      }
    }
  });

  const filterKeysWithInsufficientFilterValues = useGetFilterKeysWithInsufficientFilterValues({
    selectedBrands,
    activeFilters
  });

  const clearAllFilters = useCallback(() => {
    setActiveFilters(clearAllActiveFilters(activeFilters, settings.initialized.assortment.productFinderFilterSettings));
  }, [activeFilters, setActiveFilters, settings.initialized.assortment.productFinderFilterSettings]);

  const clearFilters = useCallback(
    (filterKeys: FilterKey<Type>[]) => {
      setActiveFilters(clearActiveFilters(activeFilters, filterKeys));
    },
    [activeFilters, setActiveFilters]
  );

  const clearFilter = useCallback(
    (filterKey: FilterKey<Type>) => {
      const { filterConfig } = getSpecConfig(productType);

      setActiveFilters(
        clearActiveFilters(activeFilters, getFilterKeysForFilterItem(filterKey, filterConfig[filterKey]))
      );
    },
    [activeFilters, productType, setActiveFilters]
  );

  const isFilterItemActive = useCallback(
    (key: FilterKey<Type>) => {
      const specConfig = getSpecConfig(productType);
      const filterEntries = getTypedFilterConfigEntries(specConfig.filterConfig);
      const filterConfigItem = filterEntries.find(entry => entry[0] === key)?.[1];
      return filterConfigItem ? isFilterItemActiveLib(key, filterConfigItem, activeFilters) : 0;
    },
    [activeFilters, productType]
  );

  const setDefaultActiveFilters = useCallback(
    (
      options: {
        overrides?: ActiveFilters<Type>;
      } = {
        overrides: {}
      }
    ) => {
      assert(query.data, "setDefaultActiveFilters: `productFilterValues` are undefined!");

      const defaultActiveFilters = getEvaluatedDefaultActiveFilters<Type>({
        assortmentType: settings.initialized.assortment.type,
        assortmentFilterSettings,
        automaticAssortmentFilter: settings.initialized.assortment.automatic?.filter,
        activeFilters,
        productFilterValues: query.data,
        filterConfig: getSpecConfig(productType).filterConfig
      });

      // TypeScript handles object merging differently for spread ({...a, ...b}) and Object.assign.
      // Spread seems to triggers strict excess property checks,
      // while Object.assign merges the incoming object types. (See type declaration)
      setActiveFilters(Object.assign({}, defaultActiveFilters, options.overrides));
    },
    [
      query.data,
      settings.initialized.assortment.type,
      assortmentFilterSettings,
      settings.initialized.assortment.automatic?.filter,
      activeFilters,
      productType,
      setActiveFilters
    ]
  );

  const getProductFiltersForBrand = useCallback(
    async (brandKey: string) => {
      return await queryClient.query({
        queryKey: queryKey({
          brandKey,
          currency: settings.initialized.currency,
          assortmentPriceSettings: settings.assortmentPriceSettings,
          filterKeys
        }),
        queryFn: () =>
          getPossibleValues({
            brandKey,
            currency: settings.initialized.currency,
            assortmentPriceSettings: settings.assortmentPriceSettings,
            filterKeys
          }),
        // Serve cached data immediately without revalidating, matching the previous `ensureQueryData` behavior
        staleTime: "static"
      });
    },
    [settings.assortmentPriceSettings, settings.initialized.currency, filterKeys, queryClient]
  );

  const setDefaultActiveFiltersForBrand = useCallback(
    async (
      brandKey: string,
      options: {
        overrides?: ActiveFilters<Type>;
      } = {
        overrides: {}
      }
    ) => {
      const productFilterValues = await getProductFiltersForBrand(brandKey);

      if (productFilterValues) {
        const defaultActiveFilters = getEvaluatedDefaultActiveFilters<Type>({
          assortmentType: settings.initialized.assortment.type,
          assortmentFilterSettings,
          automaticAssortmentFilter: settings.initialized.assortment.automatic?.filter,
          activeFilters,
          productFilterValues,
          filterConfig: getSpecConfig(productType).filterConfig
        });

        // TypeScript handles object merging differently for spread ({...a, ...b}) and Object.assign.
        // Spread seems to triggers strict excess property checks,
        // while Object.assign merges the incoming object types. (See type declaration)
        setActiveFilters(Object.assign({}, defaultActiveFilters, options.overrides));
      }
    },
    [
      getProductFiltersForBrand,
      settings.initialized.assortment.type,
      assortmentFilterSettings,
      settings.initialized.assortment.automatic?.filter,
      activeFilters,
      productType,
      setActiveFilters
    ]
  );

  return {
    query,
    activeFilters,
    selectedBrands,
    filterKeysWithInsufficientFilterValues,
    wereDefaultActiveFiltersEvaluated,
    activeFiltersCount: countActiveFilters(activeFilters),
    hasActiveFilters: hasActiveFilters(activeFilters),
    showsOnlyEBikes: showsOnlyEBikes(activeFilters),
    setActiveFilters,
    clearAllFilters,
    clearFilters,
    clearFilter,
    isFilterItemActive,
    setDefaultActiveFilters,
    getProductFiltersForBrand,
    setDefaultActiveFiltersForBrand
  };
};

const useSessionOrLocalState = <Type extends ProductType>(useSessionState: boolean) => {
  const location = useAppLocation<GlobalLocationState<Type>>();
  // Disable syncing the active filters with the session state
  const localState = React.useState<ActiveFilters<Type>>(location.state?.activeFilters ?? null);

  const [dispatch, actions] = useCommonAppDispatcher();
  const activeFilters = useCommonAppSelector(state => state.session.activeFilters as ActiveFilters<Type>);
  const setActiveFiltersCallback = React.useCallback(
    (activeFilters: ActiveFilters<Type>) => dispatch(actions.session.setActiveFilters(toStoreFilters(activeFilters))),
    [dispatch, actions.session]
  );

  if (!useSessionState) {
    return localState;
  }

  return [activeFilters, setActiveFiltersCallback] as const;
};

const queryKey = <Type extends ProductType>({
  currency,
  assortmentPriceSettings,
  filterKeys,
  brandKey
}: {
  filterKeys: FilterKey<Type>[];
  currency: Currency;
  assortmentPriceSettings: AssortmentPriceSettings;
  brandKey?: string;
}): QueryKey => ["product-filter", ...(!!brandKey ? [brandKey] : []), filterKeys, currency, assortmentPriceSettings];

const getPossibleValues = <Type extends ProductType>({
  currency,
  assortmentPriceSettings,
  filterKeys,
  brandKey
}: {
  currency: Currency;
  assortmentPriceSettings: AssortmentPriceSettings;
  filterKeys: FilterKey<Type>[];
  brandKey?: string;
}): Promise<ProductFilterValues<Type>> => {
  const filters = brandKey ? [{ key: ProductSpecKey.BrandKey, value: brandKey }] : [];
  return getPossibleValuesLib(filterKeys, currency, assortmentPriceSettings, filters);
};

export const prefetchFilterValues = async <Type extends ProductType>({
  queryClient,
  currency,
  assortmentPriceSettings,
  filterKeys,
  brandKey
}: {
  queryClient: QueryClient;
  currency: Currency;
  assortmentPriceSettings: AssortmentPriceSettings;
  filterKeys: FilterKey<Type>[];
  brandKey?: string;
}) => {
  await queryClient
    .query({
      queryKey: queryKey({ brandKey, currency, assortmentPriceSettings, filterKeys }),
      queryFn: () => getPossibleValues({ brandKey, currency, assortmentPriceSettings, filterKeys })
    })
    .catch(noop);
};

export const prefetchAllFilterValues = async <Type extends ProductType>({
  queryClient,
  currency,
  assortmentPriceSettings,
  filterKeys,
  activeBrandKeys
}: {
  queryClient: QueryClient;
  currency: Currency;
  assortmentPriceSettings: AssortmentPriceSettings;
  filterKeys: FilterKey<Type>[];
  activeBrandKeys: string[];
}) => {
  const sharedArgs = { queryClient, currency, assortmentPriceSettings, filterKeys };

  const generalFilterValues = prefetchFilterValues({
    ...sharedArgs
  });

  const brandFilterValues = activeBrandKeys.map(brandKey =>
    prefetchFilterValues({
      ...sharedArgs,
      brandKey
    })
  );

  await promiseSequence([generalFilterValues, ...brandFilterValues]);
};

export default useProductFilter;
